/**
 * Custom `characterInBrand` operator.
 *
 * Evaluates whether the session's current application (a string fact such as
 * `application_id`) belongs to the set of application slugs specified in the
 * rule value. Used by branching rules that fork the funnel path based on
 * which application (SpicyChat, PixelChat, GPTGirlfriend, SecretMate)
 * is hosting the session.
 *
 * Rule JSON shapes:
 *   Single application:
 *     { fact: "application_id", operator: "characterInBrand", value: "spicychat" }
 *   Multiple applications:
 *     { fact: "application_id", operator: "characterInBrand", value: ["spicychat", "pixelchat"] }
 *
 * Returns `false` for any non-string fact value so the rule safely
 * degrades when the brand context is not yet resolved (e.g. during the
 * initial application load).
 */

const isBrandValue = (candidate: unknown): candidate is string | ReadonlyArray<string> => {
  if (typeof candidate === "string") return true;
  if (Array.isArray(candidate)) return candidate.every((entry) => typeof entry === "string");
  return false;
};

/** json-rules-engine operator callback: `(factValue, ruleValue) => boolean`. */
const characterInBrandEvaluator = (factValue: unknown, ruleValue: unknown): boolean => {
  if (typeof factValue !== "string" || factValue.length === 0) return false;
  if (!isBrandValue(ruleValue)) return false;
  if (typeof ruleValue === "string") return factValue === ruleValue;
  return ruleValue.includes(factValue);
};

/**
 * Tuple consumed by `Engine.addOperator(name, callback)`.
 * Spread it: `engine.addOperator(...characterInBrandOperator)`.
 */
const characterInBrandOperator = ["characterInBrand", characterInBrandEvaluator] as const;

export default characterInBrandOperator;
